import React, { useState, useRef, useEffect } from 'react';
import { PageSEO } from '../components/SEO';
import { Send, Bot, User, Lock, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AskJanAI = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => 'askjan_' + Math.random().toString(36).substring(2, 15));
  const [questionCount, setQuestionCount] = useState(0);
  const [limitReached, setLimitReached] = useState(false);
  const [vipEmail, setVipEmail] = useState('');
  const [vipUnlocked, setVipUnlocked] = useState(false);
  const [vipLoading, setVipLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const FREE_LIMIT = 10;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initial welcome message
  useEffect(() => {
    setMessages([{
      role: 'assistant',
      content: "Welcome to **Ask Jan AI** — your QRM Expert Assistant.\n\nI'm trained on the complete Smart QRM Navigator knowledge base, including QRM methodology, the 2025 Award-winning case study, EBITDA ROI strategies, and more.\n\nAsk me anything about Quick Responsive Manufacturing, lead time reduction, or building your business case."
    }]);
  }, []);

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ask-jan`, {
        question: userMessage,
        session_id: sessionId
      });

      if (res.data.limit_reached) {
        setLimitReached(true);
        setQuestionCount(res.data.question_count);
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.answer }]);
        setQuestionCount(res.data.question_count);
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "I'm temporarily unavailable. Please try again or contact info@smartqrmnavigator.com." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleVipUnlock = async () => {
    if (!vipEmail || !vipEmail.includes('@')) return;
    setVipLoading(true);
    try {
      await axios.post(`${API}/ask-jan/unlock-vip`, {
        session_id: sessionId,
        email: vipEmail
      });
      setVipUnlocked(true);
      setLimitReached(false);
      setMessages(prev => [...prev, { role: 'assistant', content: "VIP access unlocked! You now have unlimited questions. How can I help you further?" }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: "Something went wrong. Please try again." }]);
    } finally {
      setVipLoading(false);
    }
  };

  const suggestedQuestions = [
    "What is QRM and how is it different from Lean?",
    "What results did the 2025 Award winner achieve?",
    "How does the EBITDA Calculator work?",
    "What is Manufacturing Critical-path Time (MCT)?"
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F9FA', fontFamily: "'IBM Plex Sans', sans-serif" }} data-testid="ask-jan-page">
      <PageSEO path="/ask-jan" />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 border border-blue-200 bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full mb-4">
            <Sparkles className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.15em] font-semibold">AI-Powered QRM Expert</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: '#0F2E58' }}>
            Ask Jan AI
          </h1>
          <p className="text-sm" style={{ color: '#6B7280' }}>
            {FREE_LIMIT - questionCount > 0 && !vipUnlocked
              ? `${FREE_LIMIT - questionCount} free questions remaining`
              : vipUnlocked
                ? 'VIP — Unlimited questions'
                : ''
            }
          </p>
        </div>

        {/* Chat Container */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" style={{ minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
          
          {/* Messages Area */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4" style={{ maxHeight: '60vh' }} data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {msg.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#0F2E58' }}>
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                <div className={`max-w-[80%] rounded-lg px-4 py-3 text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 border border-gray-200 text-gray-700'
                }`} style={{ whiteSpace: 'pre-wrap' }}
                  dangerouslySetInnerHTML={{ __html: msg.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') }}
                />
                {msg.role === 'user' && (
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 bg-blue-100">
                    <User className="h-4 w-4 text-blue-600" />
                  </div>
                )}
              </div>
            ))}

            {loading && (
              <div className="flex gap-3 justify-start">
                <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: '#0F2E58' }}>
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* VIP Gate */}
            {limitReached && !vipUnlocked && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-5 text-center" data-testid="vip-gate">
                <Lock className="h-6 w-6 mx-auto mb-2" style={{ color: '#D97706' }} />
                <p className="font-bold text-sm mb-1" style={{ color: '#0F2E58' }}>Free questions used</p>
                <p className="text-xs mb-4" style={{ color: '#6B7280' }}>Sign up for VIP access to unlock unlimited questions</p>
                <div className="flex gap-2 max-w-sm mx-auto">
                  <input
                    type="email"
                    value={vipEmail}
                    onChange={(e) => setVipEmail(e.target.value)}
                    placeholder="your@company.com"
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded"
                    data-testid="vip-unlock-email"
                  />
                  <button
                    onClick={handleVipUnlock}
                    disabled={vipLoading}
                    className="px-4 py-2 text-sm font-bold rounded transition-colors"
                    style={{ backgroundColor: '#EAB308', color: '#0A192F' }}
                    data-testid="vip-unlock-btn"
                  >
                    {vipLoading ? '...' : 'Unlock'}
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Suggested Questions (show when no user messages yet) */}
          {messages.length <= 1 && (
            <div className="px-4 sm:px-6 pb-3">
              <p className="text-xs font-medium mb-2" style={{ color: '#9CA3AF' }}>Suggested questions:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((q, i) => (
                  <button key={i} onClick={() => { setInput(q); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                    style={{ color: '#374151' }}
                    data-testid={`suggested-q-${i}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Area */}
          <div className="border-t border-gray-200 p-3 sm:p-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder={limitReached && !vipUnlocked ? "Sign up for VIP to continue..." : "Ask about QRM, lead times, EBITDA..."}
                disabled={loading || (limitReached && !vipUnlocked)}
                className="flex-1 px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                data-testid="chat-input"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim() || (limitReached && !vipUnlocked)}
                className="px-4 py-3 rounded-lg font-bold transition-all disabled:opacity-40"
                style={{ backgroundColor: '#0F2E58', color: 'white' }}
                data-testid="chat-send-btn"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Footer CTA */}
        <div className="text-center mt-8">
          <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>Want to see the numbers? Try our EBITDA Calculator.</p>
          <a href="/business-case" className="inline-flex items-center gap-2 text-sm font-bold px-6 py-2.5 rounded transition-all hover:shadow-md" style={{ backgroundColor: '#EAB308', color: '#0A192F' }} data-testid="askjan-roi-cta">
            Calculate Your ROI <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default AskJanAI;
